import React, {useEffect, useState} from 'react';

import TabbedModal from 'app/components/general/TabbedModal';
import {useCreateFolder} from 'app/folders/api';
import {createFormErrorsContext} from 'app/general/forms';
import {ItemWithChildrenStructure} from 'app/general/structures/ItemWithChildrenStructure';
import StateButton from 'app/general/widgets/StateButton';
import {KBTreeItemInnerButton} from 'app/kbs/components/KBEditPage/KBTree/KBTreeStructures';
import {gt} from 'app/utils/i18n';

import {
  FolderDescription,
  FolderName,
  FolderTeamSelector,
  FolderThumbnail,
} from './FolderForm';

import type {ActionMeta} from 'app/components/general/SweetReactSelect';
import type {FormErrorsProps} from 'app/general/forms';
import type {
  AttachedImage,
  BasicTeamSerializedType,
  FolderHashidSerializedType,
  FolderSerializedType,
} from 'sweet-types';

interface Props {
  onEditing?: (status: boolean) => Promise<any>;
  parent: FolderSerializedType | null;
  allowedTeams?: Array<BasicTeamSerializedType>;
  canEdit: boolean;
  className?: string;
}

export const CreateFolderButton = ({
  onEditing,
  parent,
  allowedTeams,
  canEdit,
  className = 'btn-link btn-sm',
}: Props) => {
  if (!canEdit) {
    return null;
  }

  const [editing, setEditing] = useState<boolean>(false);
  useEffect(() => {
    onEditing != null ? onEditing(editing) : null;
  }, [editing]);
  return (
    <ItemWithChildrenStructure>
      <KBTreeItemInnerButton
        className={className}
        itemIcon={<i className="fas fa-plus-circle" />}
        onClick={() => setEditing(true)}
      >
        {gt('Add new Folder')}
      </KBTreeItemInnerButton>
      <CreateFolderModal
        show={editing}
        onCancel={() => setEditing(false)}
        parent={parent}
        teams={allowedTeams}
        autoFocus={true}
      />
    </ItemWithChildrenStructure>
  );
};

export interface CreateFolderFormProps extends FormErrorsProps {
  onCancel: () => void;
  parent?: FolderHashidSerializedType | FolderSerializedType | null;
  teams?: Array<BasicTeamSerializedType>;
  autoFocus?: boolean;
}

export const CreateFolderForm = createFormErrorsContext<CreateFolderFormProps>(
  ({
    teams: initialTeams = [],
    parent,
    onCancel,
    autoFocus,
    captureFormErrorsFromRejection,
  }: CreateFolderFormProps) => {
    const [createFolder, {isLoading, isSuccess, isError, error}] =
      useCreateFolder();

    const [thumbnails, setThumbnails] = useState<Array<AttachedImage>>([]);
    const [name, setName] = useState<string>('');
    const [content, setContent] = useState<string>('');
    const [teams, setTeams] =
      useState<Array<BasicTeamSerializedType>>(initialTeams);

    useEffect(() => {
      if (isSuccess) {
        onCancel();
      }
      if (isError && error) {
        captureFormErrorsFromRejection(error);
      }
    }, [isSuccess, isError, error]);

    function doCreateFolder() {
      return createFolder({
        name,
        description: {content, images: thumbnails},
        teamURLs: teams.map((team) => team.url),
        parent: parent ?? undefined,
      });
    }

    function setTeamOnChange(
      teams: Array<BasicTeamSerializedType>,
      action: ActionMeta<BasicTeamSerializedType>
    ) {
      if (action.action === 'select-option') {
        setTeams((teams) => [...teams, action.option]);
      } else if (action.action === 'remove-value') {
        setTeams((teams) =>
          teams.filter((team) => team.url !== action.removedValue.url)
        );
      }
      return Promise.resolve(true);
    }

    return (
      <div>
        <div className="col-12 mb-2">
          <div className="d-flex align-items-center">
            <FolderThumbnail onChange={setThumbnails} thumbnails={thumbnails} />
            <FolderName
              autoFocus={autoFocus ?? false}
              name={name}
              onChange={setName}
            />
          </div>
          <FolderDescription
            objectId=""
            onCancel={onCancel}
            content={content}
            onChange={setContent}
          />
          <FolderTeamSelector
            teams={teams}
            allowedTeams={initialTeams}
            isSelectOnly={parent != null}
            onTeamChange={setTeamOnChange}
          />
        </div>
        <div className="mt-4 col-12 text-right">
          <button
            className="btn btn-link text-muted"
            type="button"
            onClick={onCancel}
          >
            <i className="fa fa-fw fa-times" /> {gt('Cancel')}
          </button>
          <StateButton
            onClick={doCreateFolder}
            isIdle={!isLoading}
            disabled={teams.length === 0}
          >
            {gt('Create Folder')}
          </StateButton>
        </div>
      </div>
    );
  }
);

interface CreateFolderModalProps {
  show: boolean;
  onCancel: () => void;
  parent?: FolderHashidSerializedType | FolderSerializedType | null;
  teams?: Array<BasicTeamSerializedType>;
  autoFocus?: boolean;
  zIndex?: number;
}

export function CreateFolderModal({
  show,
  onCancel,
  parent,
  teams,
  autoFocus,
  zIndex,
}: CreateFolderModalProps) {
  return (
    <TabbedModal
      show={show}
      onCancel={onCancel}
      zIndex={zIndex}
      tabs={[
        {
          eventKey: 'create-folder',
          name:
            parent == null
              ? gt('Add folder')
              : gt('Add folder in "%s" folder', parent.name),
          icon: 'far fa-folder',
          content: (
            <CreateFolderForm
              parent={parent}
              teams={teams ?? []}
              onCancel={onCancel}
              autoFocus={autoFocus}
            />
          ),
        },
      ]}
    />
  );
}
